const SCROLLABLE_CLASS = 'stats-kpi-scrollable';

// The SCSS owns the minimum width and reads the count back; neither value is duplicated here.
const MIN_WIDTH_PROPERTY = '--stats-kpi-item-min';
const VISIBLE_PROPERTY = '--stats-kpi-visible';

/** Paging geometry for one Stats/KPI widget, recomputed whenever its container resizes. */
interface StatsKpiPaging {
  visible: number;
  starts: number[];
}

/** Honours the OS reduced-motion setting for the paging animation. */
function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Resolves a CSS length to pixels, covering the rem unit the SCSS token is written in. */
function toPixels(value: string, element: HTMLElement): number {
  const amount = parseFloat(value);

  if (!isFinite(amount)) {
    return 0;
  }

  // 'rem' is tested first because it also contains 'em'.
  if (value.indexOf('rem') >= 0) {
    return amount * parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  if (value.indexOf('em') >= 0) {
    return amount * parseFloat(getComputedStyle(element).fontSize);
  }

  return amount;
}

/**
 * Derives how many whole items fit the measured container and the list of valid start indexes.
 * Container-driven, so a widget inside a narrow mega menu panel pages at its own rate.
 */
function measurePaging(widget: HTMLElement, slides: HTMLElement, items: NodeListOf<HTMLElement>): StatsKpiPaging {
  const total = items.length;
  const containerWidth = slides.clientWidth;
  const minWidth = toPixels(getComputedStyle(widget).getPropertyValue(MIN_WIDTH_PROPERTY).trim(), widget);

  // The count comes from the fixed minimum, never the rendered item width, so the resize this
  // then causes cannot change it. A collapsed (zero width) track shows everything and hides the arrows.
  const unmeasurable = containerWidth <= 0 || minWidth <= 0;
  const visible = unmeasurable ? total : Math.min(total, Math.max(1, Math.floor(containerWidth / minWidth)));

  // N % V === 0 pages cleanly by V; anything else steps by 1 to avoid a half-empty last page.
  const step = total % visible === 0 ? visible : 1;

  const starts: number[] = [];
  for (let start = 0; start <= total - visible; start += step) {
    starts.push(start);
  }

  return { visible, starts };
}

/** Wires one widget: measures, shows or hides the arrows, and pages on click. */
function initializeStatsKpi(widget: HTMLElement): void {
  const slides = widget.querySelector<HTMLElement>('.stats-kpi-slides');
  const items = widget.querySelectorAll<HTMLElement>('.stats-kpi-item');
  const previous = widget.querySelector<HTMLElement>('.stats-kpi-prev');
  const next = widget.querySelector<HTMLElement>('.stats-kpi-next');

  if (!slides || !items.length || !previous || !next) {
    return;
  }

  // All state is per widget, so several widgets on one page never share a position.
  let paging = measurePaging(widget, slides, items);

  // Publishing the count makes each item exactly 100% / V of the track, so V whole items tile it.
  const update = (): void => {
    paging = measurePaging(widget, slides, items);
    widget.style.setProperty(VISIBLE_PROPERTY, String(paging.visible));
    widget.classList.toggle(SCROLLABLE_CLASS, paging.starts.length > 1);
  };

  /** Offset of a start index from the first item, so gaps and padding are accounted for. */
  const offsetOf = (start: number): number => items[start].offsetLeft - items[0].offsetLeft;

  // Read the page back from scrollLeft rather than tracking it, so a touch swipe stays in sync.
  const currentPage = (): number => {
    let page = 0;
    let closest = Number.MAX_VALUE;
    paging.starts.forEach((start, index) => {
      const distance = Math.abs(offsetOf(start) - slides.scrollLeft);
      if (distance < closest) {
        closest = distance;
        page = index;
      }
    });
    return page;
  };

  /** Wrap-around paging, matching the loop: true convention of the Swiper carousels. */
  const goTo = (page: number): void => {
    const count = paging.starts.length;
    const target = ((page % count) + count) % count;
    slides.scrollTo({ left: offsetOf(paging.starts[target]), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  previous.addEventListener('click', () => goTo(currentPage() - 1));
  next.addEventListener('click', () => goTo(currentPage() + 1));

  update();

  if (typeof ResizeObserver === 'undefined') {
    return;
  }

  // Batching into a frame keeps the class write out of the observer callback,
  // which is what avoids "ResizeObserver loop" errors.
  let frameId = 0;
  const observer = new ResizeObserver(() => {
    if (frameId) {
      return;
    }
    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      update();
    });
  });

  // Showing the arrows narrows the track, so the observer settles the count on the next frame.
  observer.observe(slides);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLElement>('.stats-kpi').forEach(initializeStatsKpi);
});
